import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Mail, Loader2, Home, KeyRound, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

export const EmailSubscription = () => {
  const navigate = useNavigate();
  const { user, isVIP, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    day: '',
    month: '',
    year: ''
  });
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  // Auto-fill data for logged-in users
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        day: user.day?.toString() || '',
        month: user.month?.toString() || '',
        year: user.year?.toString() || ''
      }));
    }
  }, [user]);

  // Try to fill from localStorage numerologyData
  useEffect(() => {
    if (!user) {
      const storedData = localStorage.getItem('numerologyData');
      if (storedData) {
        const parsed = JSON.parse(storedData);
        setFormData(prev => ({
          ...prev,
          firstName: parsed.firstName || '',
          lastName: parsed.lastName || '',
          day: parsed.day?.toString() || '',
          month: parsed.month?.toString() || '',
          year: parsed.year?.toString() || ''
        }));
      }
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Prašome įvesti teisingą el. pašto adresą');
      return;
    }

    if (!formData.firstName) {
      toast.error('Prašome įvesti vardą');
      return;
    }

    if (!formData.day || !formData.month || !formData.year) {
      toast.error('Prašome įvesti gimimo datą');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscribe`, {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        day: parseInt(formData.day),
        month: parseInt(formData.month),
        year: parseInt(formData.year)
      });
      
      if (response.data.success) {
        setSubscribed(true);
        toast.success('Sėkmingai užsiprenumeravote!');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Įvyko klaida. Bandykite dar kartą.');
    } finally {
      setLoading(false);
    }
  };

  // VIP users don't see this component at all
  if (isVIP) {
    return null;
  }

  // Logged-in users (registered members) don't see this component
  if (isAuthenticated) {
    return null;
  }

  return (
    <Card className="card-mystical p-8 border-primary/30 shadow-glow max-w-2xl mx-auto">
      {!subscribed ? (
        <>
          <div className="text-center mb-8">
            <Mail className="w-12 h-12 text-primary mx-auto mb-4" />
            <h3 className="font-display text-2xl sm:text-3xl text-primary mb-4">
              Prisijunk prie NUMENAXIS šeimos ✨
            </h3>
            <p className="text-foreground/80 text-base leading-relaxed">
              Mes žinome, koks varginantis gali būti kasdienis skubėjimas, todėl siūlome pasirūpinti vienas kitu. 
              Nors NUMENAXIS pasaulyje skaičiai yra mūsų kalba, jie mums svarbūs tik tiek, kiek padeda geriau suprasti Jus. 
              Jei parodysite pasitikėjimą palikdami savo duomenis, NUMENAXIS šeima kas vakarą linkės Jums saldžių sapnų 
              ir per skaičių išmintį tyliai pašnibždės – kokia energija Jus pasitiks rytojaus rytas? 🌙
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName" className="text-foreground font-medium mb-2 block">
                  Vardas *
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Jūsų vardas"
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                  required
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-foreground font-medium mb-2 block">
                  Pavardė
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Jūsų pavardė"
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-foreground font-medium mb-2 block">
                El. paštas *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="jusu@elpastas.lt"
                className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                required
              />
            </div>

            {/* Birth Date */}
            <div>
              <Label className="text-foreground font-medium mb-2 block">
                Gimimo data * <span className="text-muted-foreground font-normal">(asmeninėms prognozėms)</span>
              </Label>
              <div className="grid grid-cols-3 gap-3">
                <Input
                  name="day"
                  type="number"
                  min="1"
                  max="31"
                  value={formData.day}
                  onChange={handleChange}
                  placeholder="Diena"
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                  required
                />
                <Input
                  name="month"
                  type="number"
                  min="1"
                  max="12"
                  value={formData.month}
                  onChange={handleChange}
                  placeholder="Mėnuo"
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                  required
                />
                <Input
                  name="year"
                  type="number"
                  min="1900"
                  max={new Date().getFullYear()}
                  value={formData.year}
                  onChange={handleChange}
                  placeholder="Metai"
                  className="bg-input border-border text-foreground placeholder:text-muted-foreground text-center"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              size="lg"
              className="w-full bg-primary hover:bg-primary-glow text-primary-foreground font-semibold py-6 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Siunčiama...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Prisijungti prie šeimos
                </>
              )}
            </Button>
          </form>
        </>
      ) : (
        /* Thank You Message After Subscription */
        <div className="text-center">
          <Home className="w-16 h-16 text-primary mx-auto mb-4" />
          <h3 className="font-display text-2xl sm:text-3xl text-primary mb-4">
            Ačiū už tavo pasitikėjimą – tikimės tau čia bus šilta ir jauku, kaip namie ✨
          </h3>
          <p className="text-foreground/80 text-base leading-relaxed mb-8">
            Patikrink savo el. paštą, ten jau keliauja mūsų pirmasis pasisveikinimas. 
            Kad kiekvieną kartą užsukus pas mus nereikėtų vėl pildyti duomenų ir galėtum tiesiog mėgautis akimirka, 
            kviečiame tave tapti oficialiu šeimos nariu ir užsiregistruoti. 
            Tai užtruks vos kelias sekundes, bet sutaupys tau brangaus laiko kiekvieną dieną. 🏠🗝️
          </p>
          
          {/* Registration CTA - only if not authenticated */}
          {!isAuthenticated && (
            <div className="bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 border-2 border-primary/50 rounded-lg p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <KeyRound className="w-6 h-6 text-primary" />
                <span className="text-primary font-bold text-lg">Tapk šeimos nariu</span>
              </div>
              <p className="text-foreground/80 mb-4">
                Registracija užtruks tik kelias sekundes
              </p>
              <Button 
                onClick={() => {
                  // Trigger auth modal - we need to dispatch an event or use a callback
                  const event = new CustomEvent('openAuthModal', { detail: { mode: 'register' } });
                  window.dispatchEvent(event);
                }}
                className="bg-primary hover:bg-primary-glow text-primary-foreground font-bold px-8 py-3 text-base shadow-glow"
              >
                <KeyRound className="w-5 h-5 mr-2" />
                Registruotis
              </Button>
            </div>
          )}
        </div>
      )}

      <p className="text-center text-muted-foreground text-sm mt-6">
        Kontaktai: <a href="mailto:info@numenaxis.com" className="text-primary hover:text-primary-glow">info@numenaxis.com</a>
      </p>
    </Card>
  );
};
